import { Injectable } from '@angular/core';
import { PatientConsultationListItem } from '@models/data/patient-consultations/PatientConsultationListItem';
import { take } from 'rxjs/operators';
import { PatientConsultationQueryParams } from '@models/data/patient-consultations/interfaces/PatientConsultationQueryParams';
import { ErrorHelperService } from '@services/helpers/error-helper/error-helper.service';
import { PatientConsultation } from '@models/data/patient-consultations/PatientConsultation';
import { PatientConsultationFormData } from '@models/data/patient-consultations/interfaces/PatientConsultationFormData';
import { PatientConsultationHttpService } from '@services/http/patient-consultation/patient-consultation-http.service';
import { TenantService } from '@services/core/tenant/tenant.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PatientConsultationBlService {
  constructor(
    private pcEndpoints: PatientConsultationHttpService,
    private errorHelperService: ErrorHelperService,
    private tenantService: TenantService
  ) {}

  async listFromAllTenants(
    queryParams: PatientConsultationQueryParams
  ): Promise<PatientConsultationListItem[]> {
    const usersTenants = this.tenantService.currTenantsValue;
    let patientConsultations: PatientConsultationListItem[] = [];

    if (usersTenants) {
      for (const tenant of usersTenants) {
        const patientConsultationsRes =
          await this.listForRibbonFromSpecificTenant(queryParams, tenant.id);

        const patientConsultationsFound =
          patientConsultationsRes && patientConsultationsRes.length > 0;
        if (patientConsultationsFound) {
          patientConsultations = patientConsultations.concat(
            patientConsultationsRes
          );
        }
      }
    }

    return patientConsultations;
  }

  async listForRibbonFromSpecificTenant(
    queryParams: PatientConsultationQueryParams,
    tenantId: string
  ): Promise<PatientConsultationListItem[]> {
    return firstValueFrom(
      this.pcEndpoints
        .listRibbonFromSpecificTenant(queryParams, tenantId)
        .pipe(take(1))
    ).then((res) => res.body.items);
  }

  /**
   * List in tenant by patientId ID
   * @param patientId string
   * @param tenantId string
   */
  async listFromSpecificTenantByPatientId(
    patientFitMedProfileId: string,
    tenantId: string
  ): Promise<PatientConsultationListItem[]> {
    try {
      const pcQueryParams: PatientConsultationQueryParams = {
        PatientFitMedProfileId: patientFitMedProfileId,
        MaxResultCount: 50,
        Sorting: 'patientConsultation.creationTime DESC',
      };

      return firstValueFrom(
        await this.pcEndpoints
          .listFromSpecificTenant(pcQueryParams, tenantId)
          .pipe(take(1))
      ).then((res) => res.body.items);
    } catch (e) {
      this.errorHelperService.handleErr(e);
    }
  }

  async createInTenant(
    formData: PatientConsultationFormData,
    tenantId: string
  ): Promise<PatientConsultation> {
    try {
      return await this.pcEndpoints
        .createInTenant(formData, tenantId)
        .pipe(take(1))
        .toPromise()
        .then((res) => res.body);
    } catch (e) {
      this.errorHelperService.handleErr(e);
    }
  }

  async updateInTenant(
    patientConsultId: string,
    formData: PatientConsultationFormData,
    tenantId: string
  ): Promise<PatientConsultation> {
    try {
      return await this.pcEndpoints
        .updateInTenant(patientConsultId, formData, tenantId)
        .pipe(take(1))
        .toPromise()
        .then((res) => res.body);
    } catch (e) {
      this.errorHelperService.handleErr(e);
    }
  }
}
